import { array, either, option, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { isEmptyString } from './stringHelpers';
import { emptyStringOptionToUndefined, emptyStringToUndefined } from './fp';

export type LocationProps = {
  city?: string;
  region?: string; // i.e., "state", "province"
  country?: string;
};

const COUNTRIES_TO_SHOW_REGION: string[] = [
  'US',
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
  'CA',
  'CAN',
  'CANADA',
];

const shouldCountryShowRegion = (country?: string): boolean =>
  pipe(
    country,
    option.fromNullable, // if no country, skip the rest and return false
    option.map(string.toUpperCase),
    option.map((v) => COUNTRIES_TO_SHOW_REGION.includes(v)),
    option.getOrElse(() => false),
  );

/**
 * This is exported only for separate unit testing
 * @internal
 */
export const shouldShowRegion = (region?: string, country?: string): boolean =>
  pipe(
    // if we don't have country, skip and return true
    isEmptyString(country) ? either.left(true) : either.right(region),
    // we have a country: if we don't have a region, return false. Otherwise, return shouldCountryShowRegion
    either.chain((r) =>
      isEmptyString(r) ? either.left(false) : either.right(shouldCountryShowRegion(country)),
    ),
    either.toUnion,
  );

// Several steps to ensure the set location values are non-empty strings
const cleanLocation = (location: LocationProps = {}) =>
  pipe(
    location,
    (value) => value as Record<string, string>,
    record.map(option.fromNullable),
    record.map(option.map((v) => `${v}`)), // Make sure everything is a string because of customer data
    record.map(option.map(string.trim)),
    record.map(emptyStringOptionToUndefined), // Ensure empty strings are cast back to undefined
    record.map(option.fromNullable),
    record.filter(option.isSome), // remove empty values
    record.map(option.getOrElse(() => string.empty)), // This should not happen, but the step is here to get the existing values only
  );

const getLocationProperty =
  (key: keyof LocationProps) =>
  (location: Record<string, string>): string =>
    pipe(
      location,
      record.lookup(key),
      option.getOrElse(() => string.empty),
    );

const getLocationCity = getLocationProperty('city');
const getLocationRegion = getLocationProperty('region');
const getLocationCountry = getLocationProperty('country');

const getLocationTail = (location: Record<string, string>): string =>
  pipe([getLocationCountry, getLocationRegion], array.flap(location), ([country, region]) =>
    shouldShowRegion(region, country) ? region : country,
  );

export const formatLocation = (location?: LocationProps): string =>
  pipe(
    [getLocationCity, getLocationTail],
    array.flap(cleanLocation(location)), // pass a cleaned up copy of the location
    array.map(emptyStringToUndefined), // ensure the string values are not empty
    array.map(option.fromNullable), // cast to option
    array.compact, // in order to remove empty values
    (v) => v.join(', '), // always return a string
  );
